import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const RegisterPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [betaCode, setBetaCode] = useState('');
  const [tosAccepted, setTosAccepted] = useState(false);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const { register } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => { document.title = 'Create account — Sedra'; }, []);

  // Password validation
  const validatePassword = (password: string) => {
    const errors: string[] = [];
    if (password.length < 6 || password.length > 12) {
      errors.push('6-12 characters');
    }
    if (!/\d/.test(password)) {
      errors.push('at least 1 number');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push('at least 1 symbol');
    }
    setPasswordErrors(errors);
    return errors.length === 0;
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (newPassword) {
      validatePassword(newPassword);
    } else {
      setPasswordErrors([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Client-side validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (passwordErrors.length > 0) {
      setError('Please fix password requirements');
      return;
    }

    // Legal acceptance validation (REQUIRED - no exceptions)
    if (!tosAccepted || !privacyPolicyAccepted) {
      setError('You must accept both Terms of Service and Privacy Policy to register');
      return;
    }

    setIsSubmitting(true);
    try {
      await register(email, password, confirmPassword, betaCode, tosAccepted, privacyPolicyAccepted);
      // Go to branded "Check your email" screen
      navigate(`/check-email?email=${encodeURIComponent(email)}`);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-md px-6 pt-16">
        <header className="text-center">
          <img src="/logo-color.svg" alt="" aria-hidden="true" className="mx-auto h-8 w-auto" />
          <h1 className="display-hero mt-6 text-3xl font-extrabold tracking-tight text-gray-900">Create your account</h1>
          <p className="mt-2 text-sm text-gray-600">
            Already have an account? <a href="/login" className="text-sedra hover:underline">Sign in</a>
          </p>
        </header>

        <main id="main" className="mt-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 elev-1">
            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-4">
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    aria-describedby={error ? 'register-error' : undefined}
                    className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
                  />
                </div>

                {/* Password */}
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPwd ? 'text' : 'password'}
                      required
                      autoComplete="new-password"
                      value={password}
                      onChange={handlePasswordChange}
                      aria-describedby={error ? 'register-error' : undefined}
                      className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 pr-12 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(v => !v)}
                      aria-label={showPwd ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-3 my-auto h-8 px-2 rounded-md text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sedra"
                    >
                      {showPwd ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  <p className={`mt-1 text-xs ${passwordErrors.length > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                    {passwordErrors.length > 0
                      ? `Requirements: ${passwordErrors.join(', ')}`
                      : '6-12 characters, 1 number, 1 symbol'}
                  </p>
                </div>

                {/* Confirm Password */}
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">Confirm password</label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirm ? 'text' : 'password'}
                      required
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      aria-describedby={error ? 'register-error' : undefined}
                      className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 pr-12 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(v => !v)}
                      aria-label={showConfirm ? 'Hide password' : 'Show password'}
                      className="absolute inset-y-0 right-3 my-auto h-8 px-2 rounded-md text-gray-500 hover:text-gray-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-sedra"
                    >
                      {showConfirm ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-1 text-xs text-red-600">Passwords do not match</p>
                  )}
                </div>

                {/* Beta Code */}
                <div>
                  <label htmlFor="betaCode" className="block text-sm font-medium text-gray-700">Beta Access Code</label>
                  <input
                    id="betaCode"
                    name="betaCode"
                    type="text"
                    required
                    placeholder="Enter your beta access code"
                    value={betaCode}
                    onChange={e => setBetaCode(e.target.value.trimStart())}
                    className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
                  />
                </div>

                {/* Legal Acceptance Section */}
                <div className="space-y-2 pt-2">
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-sedra focus:ring-sedra"
                      checked={tosAccepted}
                      onChange={e => setTosAccepted(e.target.checked)}
                      required
                    />
                    <span className="text-sm text-gray-700">
                      I accept the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline">Terms of Service</a>
                    </span>
                  </label>

                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border-gray-300 text-sedra focus:ring-sedra"
                      checked={privacyPolicyAccepted}
                      onChange={e => setPrivacyPolicyAccepted(e.target.checked)}
                      required
                    />
                    <span className="text-sm text-gray-700">
                      I accept the <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline">Privacy Policy</a>
                    </span>
                  </label>
                </div>

                {/* Inline error/success */}
                {error && (
                  <div id="register-error" role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                  </div>
                )}
                {success && (
                  <div role="status" className="rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                    {success}
                  </div>
                )}

                {/* Submit button */}
                <button
                  type="submit"
                  aria-busy={isSubmitting}
                  disabled={!email || !password || !confirmPassword || !betaCode || passwordErrors.length > 0 || !tosAccepted || !privacyPolicyAccepted}
                  className="w-full rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
                >
                  {isSubmitting ? 'Creating account…' : 'Register'}
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
};

export default RegisterPage; 